import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';

import {
  AuctionVehiclesFilterRequestBody,
  GetAuctionVehicleListApiArg,
  useGetSourcingAuctionListQuery,
} from '@dms/api/core';

type UseAuctionsVehicleListQueriesProps = {
  filterBody: AuctionVehiclesFilterRequestBody | null;
  orderBy: GetAuctionVehicleListApiArg['orderBy'];
  currency: GetAuctionVehicleListApiArg['currency'];
  isFiltersOpen: boolean;
  isRealTime: boolean;
  safePage: number;
  pollingInterval: number;
  skip?: boolean;
};

export function useAuctionsVehicleListQueries(props: UseAuctionsVehicleListQueriesProps) {
  // Polling: silently refetches page 1 at the given interval when autorefresh is enabled.
  const {fulfilledTimeStamp} = useGetSourcingAuctionListQuery(
    isNotNil(props.filterBody)
      ? {
          page: 1,
          isPolling: true,
          orderBy: props.orderBy,
          currency: props.currency,
          body: props.filterBody,
        }
      : skipToken,
    {
      skip: props.skip || props.isFiltersOpen || !props.isRealTime,
      pollingInterval: props.isRealTime ? props.pollingInterval : undefined,
      skipPollingIfUnfocused: true,
      refetchOnFocus: props.isRealTime,
      selectFromResult: ({fulfilledTimeStamp: pollingFulfilledTimeStamp}) => ({
        fulfilledTimeStamp: pollingFulfilledTimeStamp,
      }),
    }
  );

  // Infinite scroll: fetches based on current page, data accumulates via RTK Query merge.
  const {data, currentData, isLoading, isFetching, isError, error} = useGetSourcingAuctionListQuery(
    isNotNil(props.filterBody)
      ? {
          page: props.safePage,
          isPolling: false,
          orderBy: props.orderBy,
          currency: props.currency,
          body: props.filterBody,
        }
      : skipToken,
    {skip: props.skip || props.isFiltersOpen}
  );

  return {fulfilledTimeStamp, data, currentData, isLoading, isFetching, isError, error};
}
